'use client';

import { Lightbulb, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/shadcn/dialog';
import { Typography } from '@/shared/ui/shadcn/typography';
import type { ProjectGoalsGuide } from '@/types/project';

interface GoalsBriefGuideProps {
  guide: ProjectGoalsGuide['guide'];
  /** Optional extra note shown at the top of the modal (e.g. "write in your
   *  native language"). */
  note?: string;
  /** Controlled mode — when `open` is provided the component renders no inline
   *  lightbulb trigger; the caller opens it (e.g. from a "?" icon next to the
   *  section title). When omitted, the legacy inline lightbulb callout is shown
   *  (still used by the render-settings goals sheet). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * "How to write a great brief" modal guide. All copy is server-driven (see
 * useProjectGoalsGuide) — the app analyzes the written task (not just the
 * photo), so a rich brief materially improves results. Mirrors Apple/Android.
 */
export function GoalsBriefGuide({
  guide,
  note,
  open,
  onOpenChange,
}: GoalsBriefGuideProps) {
  const controlled = open !== undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {!controlled && (
        <DialogTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-3 rounded-xl border border-border bg-transparent px-4 py-3 text-start transition-colors hover:bg-muted/40"
          >
            <Lightbulb className="h-5 w-5 shrink-0 text-primary" />
            <span className="flex-1 text-list-item-title">{guide.trigger}</span>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground rtl:-scale-x-100" />
          </button>
        </DialogTrigger>
      )}
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg sm:p-8 md:p-10">
        <DialogHeader>
          <DialogTitle>{guide.title}</DialogTitle>
        </DialogHeader>

        {note && (
          <Typography variant="muted" className="text-form-help font-medium">
            {note}
          </Typography>
        )}

        <Typography variant="muted" className="text-form-help">
          {guide.preamble}
        </Typography>

        <ul className="space-y-4">
          {guide.bullets.map((bullet, index) => (
            <li key={index} className="flex flex-col gap-0.5">
              <span className="text-list-item-title text-foreground">
                {bullet.title}
              </span>
              <Typography variant="muted" className="text-form-help">
                {bullet.body}
              </Typography>
            </li>
          ))}
        </ul>

        <Typography
          variant="muted"
          className="text-form-help font-medium text-foreground"
        >
          {guide.closing}
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
